// src/services/dossierInscriptionServices.js
import axios from "axios";

const apiUrl = `${process.env.REACT_APP_API_URL}/api/dossier-inscription`;

function authHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

//// GET

// Récupérer le dossier d'inscription d'un adhérent (par inscription_adherent_id)
export const getDossierInscription = async (adherentId) => {
  try {
    const { data } = await axios.get(`${apiUrl}/adhesion/${adherentId}`, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error("Erreur lors du chargement du dossier d'inscription:", error);
    throw error;
  }
};

// Récupérer tous les dossiers d'inscription
export const getAllDossiersInscription = async () => {
  try {
    const { data } = await axios.get(apiUrl, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error(
      "Erreur lors du chargement des dossiers d'inscription:",
      error
    );
    throw error;
  }
};

// Obtenir l'URL de la licence assurance pour un inscription_adherent_id
export const getLicenceAssuranceUrl = async (inscriptionAdherentId) => {
  try {
    const { data } = await axios.get(
      `${apiUrl}/licence-url/${inscriptionAdherentId}`,
      { headers: { ...authHeaders() } }
    );
    return data; // string (ex: "/uploads/dossier_inscription/123/licence-...pdf")
  } catch (error) {
    console.error(
      "Erreur lors de la récupération de la licence assurance:",
      error
    );
    throw error;
  }
};

//// POST

// Créer (ou récupérer s'il existe déjà) un dossier d'inscription pour un adhérent
export const createDossierInscription = async (
  inscriptionAdherentId,
  extra = {}
) => {
  if (!inscriptionAdherentId) {
    throw new Error("inscriptionAdherentId requis");
  }

  const payload = {
    inscription_adherent_id: inscriptionAdherentId,
    // Valeurs par défaut (tu peux les écraser via `extra`)
    validate_cm: false,
    validate_licence: false,
    validate_reglement: false,
    ...extra, // ex: { lien_drive, cm_url, licence_url, ... } si tu en as besoin
  };

  try {
    const { data } = await axios.post(`${apiUrl}/createdossier`, payload, {
      headers: { ...authHeaders() },
    });
    return data; // 201 si créé, 200 si déjà existant (selon ton contrôleur)
  } catch (error) {
    console.error(
      "Erreur lors de la création du dossier d'inscription:",
      error
    );
    throw error;
  }
};

// Créer un nouveau dossier à la validation du formulaire d'adhésion
export const createNewDossierInscription = async (inscriptionAdherentId) => {
  try {
    const { data } = await axios.post(
      `${apiUrl}/create-new-dossier-at-adhesion`,
      { inscription_adherent_id: inscriptionAdherentId },
      { headers: { ...authHeaders() } }
    );
    return data;
  } catch (error) {
    console.error(
      "Erreur lors de la création du dossier d'inscription:",
      error
    );
    throw error;
  }
};

//// PATCH (JSON)

// Mettre à jour un dossier (champs JSON)
export const updateDossierInscription = async (dossierId, payload) => {
  const url = `${apiUrl}/updatedossier/${dossierId}`;
  const headers = { ...authHeaders() };

  console.log("[DS] updateDossierInscription →", {
    url,
    dossierId,
    payload,
    headers,
  });

  try {
    const { data, status } = await axios.patch(url, payload, { headers });
    console.log("[DS] updateDossierInscription ←", { status, data });
    return data;
  } catch (error) {
    if (error.response) {
      console.error("[DS] updateDossierInscription ERROR (response)", {
        status: error.response.status,
        data: error.response.data,
        url,
        dossierId,
        payload,
      });
    } else if (error.request) {
      console.error("[DS] updateDossierInscription ERROR (request)", {
        url,
        dossierId,
        payload,
        message: error.message,
      });
    } else {
      console.error("[DS] updateDossierInscription ERROR", {
        url,
        dossierId,
        payload,
        message: error.message,
      });
    }
    throw error;
  }
};

//// PATCH (UPLOADS) — NE PAS définir Content-Type, Axios s’en charge avec FormData

// 1) Upload Certificat médical / Attestation santé (champ: "cm_url")
export const uploadCertificatMedical = async (dossierId, file, extra = {}) => {
  try {
    const fd = new FormData();
    fd.append("cm_url", file); // DOIT matcher .single("cm_url") côté route
    if (extra.date_cm) fd.append("date_cm", extra.date_cm); // on envoie la date en même temps

    const { data } = await axios.patch(
      `${apiUrl}/${dossierId}/upload-certificat-medical`,
      fd,
      { headers: { ...authHeaders() } } // ne PAS fixer Content-Type
    );
    return data;
  } catch (error) {
    console.error("Erreur lors de l'upload du certificat médical:", error);
    throw error;
  }
};

// Formulaire de licence signé — upload (champ: "formulaire_licence_signe")
export const uploadFormulaireLicenceSigne = async (dossierId, file) => {
  try {
    const fd = new FormData();
    fd.append("formulaire_licence_signe", file);
    const { data } = await axios.patch(
      `${apiUrl}/${dossierId}/upload-formulaire-licence-signe`,
      fd,
      { headers: { ...authHeaders() } }
    );
    return data; // { ok, formulaire_licence_signe }
  } catch (error) {
    console.error("Erreur lors de l'upload du formulaire signé:", error);
    throw error;
  }
};

// Licence/assurance définitive — upload (champ: "licence_url")
export const uploadLicenceDefinitive = async (dossierId, file) => {
  try {
    const fd = new FormData();
    fd.append("licence_url", file);
    const { data } = await axios.patch(
      `${apiUrl}/${dossierId}/upload-licence-definitive`,
      fd,
      { headers: { ...authHeaders() } }
    );
    return data; // { ok, licence_url }
  } catch (error) {
    console.error("Erreur lors de l'upload de la licence définitive:", error);
    throw error;
  }
};

/** Nettoyage DB d’un champ fichier (met à null).
 *  NB: Si tu veux aussi supprimer physiquement le fichier sur le serveur,
 *  implémente un endpoint dédié côté back qui unlink() le fichier avant de nullifier.
 */
export const deleteDossierFile = async (dossierId, field) => {
  // field ∈ ["cm_url","formulaire_licence_a_signer","formulaire_licence_signe","licence_url","lien_drive"]
  const { data } = await axios.post(
    `${apiUrl}/${dossierId}/remove-file`,
    { field },
    { headers: { ...authHeaders() } }
  );
  return data; // { ok, id, field, previous }
};

//// DELETE

// Supprimer un dossier d'inscription
export const deleteDossierInscription = async (dossierId) => {
  try {
    const { data } = await axios.delete(`${apiUrl}/${dossierId}`, {
      headers: { ...authHeaders() },
    });
    return data;
  } catch (error) {
    console.error(
      "Erreur lors de la suppression du dossier d'inscription:",
      error
    );
    throw error;
  }
};

// Définir cm_url via un lien externe (ex: Google Drive)
export const setCertificatMedicalLink = async (dossierId, url) => {
  if (!dossierId || !url) throw new Error("dossierId et url requis");
  const payload = {
    cm_url: url,
    date_cm: new Date().toISOString(), // pour afficher la date de dépôt
  };
  return await updateDossierInscription(dossierId, payload);
};

/** Dossier FS : existe ? */
export const hasAdherentFolder = async (adherentId) => {
  const { data } = await axios.get(
    `${apiUrl}/adherent/${adherentId}/has-folder`,
    { headers: { ...authHeaders() } }
  );
  return data; // { exists: boolean }
};

/** Dossier FS : créer si manquant */
export const ensureAdherentFolder = async (adherentId) => {
  const { data } = await axios.post(
    `${apiUrl}/adherent/${adherentId}/ensure-folder`,
    {},
    { headers: { ...authHeaders() } }
  );
  return data; // { created: boolean }
};
